export type DefinitionPart = {
    text: string
    bold?: boolean
}

export type UnitColumnDefinition = {
    label: string
    parts: DefinitionPart[]
    isFoundation?: boolean
}

/** Shared label and phrase — use exact wording wherever this concept is referenced. */
export const NEEDS_SERVICE_LABEL = 'Needs Service'
export const NEEDS_SERVICE_PHRASE = 'units that need service'
export const UNIT_NEEDS_SERVICE_PHRASE = 'unit that needs service'

export const READY_UNIT_LABEL = 'Ready Unit'
export const UNIT_READY_PHRASE = 'ready unit'
export const READY_UNITS_PHRASE = 'ready units'
export const IN_READINESS_SCOPE_PHRASE = 'units in readiness scope'

/** Label on the toolbar button — keep in sync with ProjectsTab. */
export const COLUMN_DEFINITIONS_BUTTON_LABEL = 'Column Definitions'

export const COLUMN_DEFINITIONS_HEADER_TOOLTIP = `Click ${COLUMN_DEFINITIONS_BUTTON_LABEL} in the toolbar to see the full definition for this column.`

export const UNIT_COLUMN_DEFINITIONS: UnitColumnDefinition[] = [
    {
        label: NEEDS_SERVICE_LABEL,
        isFoundation: true,
        parts: [
            {
                text: 'A ',
            },
            {
                text: UNIT_NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text:
                    ' has at least one schedulable, occupiable area on this project whose area status is marked “requires service” (per your apartment’s status configuration, e.g. V, TI, TO, EM). The metrics below only apply to this set of units unless noted otherwise.',
            },
        ],
    },
    {
        label: READY_UNIT_LABEL,
        isFoundation: true,
        parts: [
            {
                text:
                    'A unit is ready if all of the Workorders are in a completed status. Completed statuses include: Complete, Pre-approved, Approved, Invoice request, Invoiced, and Invoiced out. Additionally, a ',
            },
            {
                text: UNIT_NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text:
                    ' that does not have any WorkOrders on this project is not a ',
            },
            {
                text: UNIT_READY_PHRASE,
                bold: true,
            },
            {
                text: '.',
            },
        ],
    },
    {
        label: 'Units Unassigned',
        parts: [
            {
                text: 'The count of ',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text: ' that do not yet have a work order on this project.',
            },
        ],
    },
    {
        label: 'Units Scheduled',
        parts: [
            {
                text: 'Among ',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text:
                    ', what percentage have at least one work order. The calculation is (',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text: ' with a work order) ÷ (',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text:
                    '). Units on the project that do not need service are not included in this percentage. The tooltip shows this as assigned ÷ need service (e.g. 85/100). Target is 100%. A (+N) suffix counts additional units that have work orders but are not ',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text: '.',
            },
        ],
    },
    {
        label: 'Units Ready',
        parts: [
            {
                text: 'Among the ',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text:
                    ', plus any other unit with at least one work order on this project, what percentage are ',
            },
            {
                text: READY_UNITS_PHRASE,
                bold: true,
            },
            {
                text: '. Calculated as (',
            },
            {
                text: READY_UNITS_PHRASE,
                bold: true,
            },
            {
                text: ') ÷ (',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text:
                    '+ any unit with an assigned Workorder). Tooltip shows ready ÷ in-scope (e.g. 60/115). This is broader than Units Scheduled, which only measures coverage over ',
            },
            {
                text: NEEDS_SERVICE_PHRASE,
                bold: true,
            },
            {
                text: '.',
            },
        ],
    },
    {
        label: 'Unit Turn %',
        parts: [
            {
                text:
                    'Share of the property’s units included in this turn. Calculated as (turn units that are schedule-visible and have at least one occupiable area) ÷ (all such units on the property).',
            },
        ],
    },
    {
        label: 'Max Vendor Output',
        parts: [
            {
                text:
                    'Average work orders per day for the busiest vendor-service pair on this project. Only vendors completing the services of paint, clean, or carpet are included. Calculated as the work-order count ÷ their planned work window. Shown as a pace/pressure signal for staffing. For these trades, values above about 10/day have been associated with lower completion success in internal turn data. Light or specialty services outside paint/clean/carpet are not included.',
            },
        ],
    },
]
